// Revise run — the one state machine behind every profile.
//
// The renderer is a subscriber to this hook; nothing about scoring lives in a
// component. Every answer is kept in AUTHORED indices (never letters), every
// assessed submit is reported once through `onQuestionSubmit`, and completion
// fires exactly once from `onResults` when the results screen is reached —
// the single completion point the plan asks for (§3.4).
//
// Resume: `initialProgress` is the learner's latest row per activity key (from
// services/reviseService). Answers restore as submitted; a stored `__results`
// row restores straight to the results screen.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MtaScreen, QuestionScreen, ReviseRun, ReviseScreen } from '../../../../../lib/revise/types';
import { REVISE_RESULTS_KEY, assessedQuestions, questionTimeSec } from '../../../../../lib/revise/types';
import {
  gradeQuestion,
  gradeMta,
  summarise,
  seededOrder,
  type Grade,
  type MtaResponse,
  type QuestionResponse,
  type ReviseSummary,
} from '../../../../../lib/revise/scoring';

export type RunPhase = 'cover' | 'run' | 'results' | 'review';

export interface EvidenceState {
  chosen: number[];
  checked: boolean;
}

export interface BuilderState {
  /** slot index → chosen correct option index (once found). */
  filled: Record<number, number>;
  /** slot index → wrong option indices already tried (locked). */
  tried: Record<number, number[]>;
}

export interface ReviseRunCallbacks {
  onQuestionSubmit?: (q: QuestionScreen, r: QuestionResponse, g: Grade) => void;
  onMtaCheck?: (m: MtaScreen, r: MtaResponse) => void;
  onResults?: (summary: ReviseSummary, payload: Record<string, unknown>) => void;
  onAdvance?: () => void;
}

export interface UseReviseRunOptions extends ReviseRunCallbacks {
  run: ReviseRun;
  initialProgress?: Record<string, Record<string, unknown>> | null;
}

const isQuestion = (s: ReviseScreen | undefined): s is QuestionScreen => !!s && s.type === 'question';

/** Rebuild a QuestionResponse from a stored tracking payload. */
function responseFromPayload(p: Record<string, unknown>): QuestionResponse | null {
  const r: QuestionResponse = {};
  if (Array.isArray(p.selected)) r.selected = (p.selected as unknown[]).map(Number);
  if (Array.isArray(p.sequence)) r.sequence = (p.sequence as unknown[]).map(Number);
  if (p.placement && typeof p.placement === 'object') r.placement = p.placement as Record<string, number>;
  if (typeof p.seconds === 'number') r.seconds = p.seconds;
  return r.selected || r.sequence || r.placement ? r : null;
}

export function useReviseRun({ run, initialProgress, onQuestionSubmit, onMtaCheck, onResults, onAdvance }: UseReviseRunOptions) {
  const screens = run.screens;
  const timed = run.profile === 'paper' && run.paper?.timed !== false;

  const [phase, setPhase] = useState<RunPhase>('cover');
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, QuestionResponse>>({});
  const [draft, setDraft] = useState<Record<string, QuestionResponse>>({});
  const [mta, setMta] = useState<Record<string, MtaResponse>>({});
  const [mtaChecked, setMtaChecked] = useState<Record<string, boolean>>({});
  const [builder, setBuilder] = useState<Record<string, BuilderState>>({});
  const [picks, setPicks] = useState<Record<string, number>>({});
  const [evidence, setEvidence] = useState<Record<string, EvidenceState>>({});
  const [revealed, setRevealed] = useState<Record<string, number>>({});
  const [review, setReview] = useState<{ queue: string[]; pos: number }>({ queue: [], pos: 0 });
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [qSeconds, setQSeconds] = useState(0);
  const resultsFired = useRef(false);
  const restored = useRef(false);

  // --- Resume ---------------------------------------------------------------
  useEffect(() => {
    if (restored.current || !initialProgress) return;
    restored.current = true;
    const a: Record<string, QuestionResponse> = {};
    const m: Record<string, MtaResponse> = {};
    const mc: Record<string, boolean> = {};
    for (const s of screens) {
      const p = initialProgress[s.id];
      if (!p) continue;
      if (s.type === 'question') {
        const r = responseFromPayload(p);
        if (r) a[s.id] = r;
      } else if (s.type === 'mta' && p.verdicts && typeof p.verdicts === 'object') {
        m[s.id] = { verdicts: p.verdicts as MtaResponse['verdicts'] };
        mc[s.id] = true;
      }
    }
    if (Object.keys(a).length) setAnswers(a);
    if (Object.keys(m).length) {
      setMta(m);
      setMtaChecked(mc);
    }
    if (initialProgress[REVISE_RESULTS_KEY]) {
      resultsFired.current = true; // already recorded; do not fire completion again
      setPhase('results');
    } else if (Object.keys(a).length || Object.keys(m).length) {
      // Any restored progress resumes the run — a judged Mark This Answer with
      // no question answered yet is still progress.
      // Land on the first unanswered assessed question, or the last screen touched.
      const firstOpen = screens.findIndex((s) => s.type === 'question' && s.assessed && !a[s.id]);
      setIdx(firstOpen >= 0 ? firstOpen : 0);
      setPhase('run');
    }
  }, [initialProgress, screens]);

  // --- Timers (paper profile) ------------------------------------------------
  const current = screens[idx];
  const currentAnswered = isQuestion(current) ? !!answers[current.id] : false;
  useEffect(() => {
    if (!timed || phase !== 'run') return;
    const t = setInterval(() => {
      setTotalSeconds((s) => s + 1);
      if (isQuestion(current) && !currentAnswered) setQSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [timed, phase, current, currentAnswered]);
  useEffect(() => {
    setQSeconds(0);
  }, [idx]);

  // --- Shuffle ---------------------------------------------------------------
  const orders = useMemo(() => {
    const out: Record<string, number[]> = {};
    for (const s of screens) {
      if (s.type === 'question' && s.options) out[s.id] = seededOrder(s.options.length, `${run.slug ?? 'run'}|${s.id}`);
    }
    return out;
  }, [screens, run.slug]);

  // --- Summary ---------------------------------------------------------------
  const summary = useMemo(() => summarise(run, answers), [run, answers]);

  // --- Actions ---------------------------------------------------------------
  const begin = useCallback(() => {
    setPhase('run');
    setIdx(0);
  }, []);

  const setDraftFor = useCallback((id: string, r: QuestionResponse) => {
    setDraft((d) => ({ ...d, [id]: { ...d[id], ...r } }));
  }, []);

  const submit = useCallback(
    (q: QuestionScreen) => {
      const r: QuestionResponse = { ...(draft[q.id] ?? {}) };
      if (timed) r.seconds = qSeconds;
      const g = gradeQuestion(q, r);
      setAnswers((a) => ({ ...a, [q.id]: r }));
      if (q.assessed) onQuestionSubmit?.(q, r, g);
      return g;
    },
    [draft, timed, qSeconds, onQuestionSubmit],
  );

  const setVerdict = useCallback((m: MtaScreen, criterionId: string, v: 'yes' | 'partial' | 'no') => {
    setMta((prev) => ({ ...prev, [m.id]: { verdicts: { ...(prev[m.id]?.verdicts ?? {}), [criterionId]: v } } }));
  }, []);

  const checkMta = useCallback(
    (m: MtaScreen) => {
      const r = mta[m.id] ?? { verdicts: {} };
      setMtaChecked((c) => ({ ...c, [m.id]: true }));
      onMtaCheck?.(m, r);
      return gradeMta(m, r);
    },
    [mta, onMtaCheck],
  );

  const chooseBuilder = useCallback((id: string, slot: number, option: number, correct: boolean) => {
    setBuilder((b) => {
      const cur = b[id] ?? { filled: {}, tried: {} };
      if (correct) return { ...b, [id]: { ...cur, filled: { ...cur.filled, [slot]: option } } };
      const tried = cur.tried[slot] ?? [];
      return { ...b, [id]: { ...cur, tried: { ...cur.tried, [slot]: tried.includes(option) ? tried : [...tried, option] } } };
    });
  }, []);

  const choosePick = useCallback((id: string, option: number) => setPicks((p) => ({ ...p, [id]: option })), []);

  const toggleEvidence = useCallback((id: string, item: number) => {
    setEvidence((e) => {
      const cur = e[id] ?? { chosen: [], checked: false };
      if (cur.checked) return e;
      const chosen = cur.chosen.includes(item) ? cur.chosen.filter((i) => i !== item) : [...cur.chosen, item];
      return { ...e, [id]: { ...cur, chosen } };
    });
  }, []);
  const checkEvidence = useCallback((id: string) => setEvidence((e) => ({ ...e, [id]: { ...(e[id] ?? { chosen: [] }), checked: true } })), []);

  const revealNext = useCallback((id: string, total: number) => setRevealed((r) => ({ ...r, [id]: Math.min(total, (r[id] ?? 1) + 1) })), []);

  /** Is the learner allowed to leave this screen? */
  const canContinue = useMemo(() => {
    if (!current) return true;
    switch (current.type) {
      case 'question':
        return run.profile === 'paper' || !!answers[current.id];
      case 'mta':
        return !!mtaChecked[current.id];
      case 'builder': {
        const b = builder[current.id];
        return !!b && current.slots.every((_, i) => b.filled[i] !== undefined);
      }
      case 'pick':
        return picks[current.id] !== undefined;
      case 'evidence':
        return !!evidence[current.id]?.checked;
      default:
        return true;
    }
  }, [current, run.profile, answers, mtaChecked, builder, picks, evidence]);

  const finish = useCallback(() => {
    setPhase('results');
  }, []);

  // Fire results exactly once when the results screen is first reached.
  useEffect(() => {
    if (phase !== 'results' || resultsFired.current) return;
    resultsFired.current = true;
    const payload: Record<string, unknown> = {
      questionId: REVISE_RESULTS_KEY,
      profile: run.profile,
      board: run.board,
      slug: run.slug,
      earned: summary.earned,
      total: summary.total,
      pct: summary.pct,
      /** 0–1, what the learning_interactions row stores as its score. */
      score: summary.total ? summary.earned / summary.total : summary.pct / 100,
      answered: summary.answered,
      assessed: summary.assessed,
      byTag: summary.byTag,
      wrong: summary.wrong.map((w) => w.id),
      slow: summary.slow,
      nscLevel: summary.nscLevel,
      levels: summary.levels,
      band: summary.band?.label,
      totalSeconds,
    };
    if (run.check) {
      payload.pairKey = run.check.pairKey;
      payload.sitting = run.check.sitting;
      const slots: Record<string, boolean> = {};
      for (const q of assessedQuestions(run)) if (q.slot !== undefined) slots[String(q.slot)] = gradeQuestion(q, answers[q.id]).correct;
      payload.slots = slots;
    }
    onResults?.(summary, payload);
  }, [phase, run, summary, answers, totalSeconds, onResults]);

  const next = useCallback(() => {
    if (phase === 'review') {
      setReview((r) => (r.pos + 1 < r.queue.length ? { ...r, pos: r.pos + 1 } : r));
      return;
    }
    if (idx + 1 >= screens.length) finish();
    else setIdx(idx + 1);
  }, [phase, idx, screens.length, finish]);

  const prev = useCallback(() => {
    if (phase === 'review') {
      setReview((r) => ({ ...r, pos: Math.max(0, r.pos - 1) }));
      return;
    }
    if (idx > 0) setIdx(idx - 1);
  }, [phase, idx]);

  const startReview = useCallback(() => {
    const queue = summary.wrong.map((w) => w.id);
    if (queue.length === 0) return;
    setReview({ queue, pos: 0 });
    setPhase('review');
  }, [summary.wrong]);
  const exitReview = useCallback(() => setPhase('results'), []);

  const restart = useCallback(() => {
    setAnswers({});
    setDraft({});
    setMta({});
    setMtaChecked({});
    setBuilder({});
    setPicks({});
    setEvidence({});
    setRevealed({});
    setTotalSeconds(0);
    resultsFired.current = false;
    setIdx(0);
    setPhase('run');
  }, []);

  const reviewScreen = phase === 'review' ? screens.find((s) => s.id === review.queue[review.pos]) : undefined;
  const timeSecFor = (q: QuestionScreen) => questionTimeSec(q);

  return {
    phase,
    idx,
    screen: phase === 'review' ? reviewScreen : current,
    screens,
    answers,
    draft,
    mta,
    mtaChecked,
    builder,
    picks,
    evidence,
    revealed,
    orders,
    summary,
    review,
    timed,
    totalSeconds,
    qSeconds,
    timeSecFor,
    canContinue,
    begin,
    next,
    prev,
    finish,
    setDraftFor,
    submit,
    setVerdict,
    checkMta,
    chooseBuilder,
    choosePick,
    toggleEvidence,
    checkEvidence,
    revealNext,
    startReview,
    exitReview,
    restart,
    onAdvance,
  };
}

export type ReviseRunApi = ReturnType<typeof useReviseRun>;
